<template>
  <div class="def">
    <!-- ===================== BARRA DE CONTROL ===================== -->
    <div class="def-control">
      <div class="dc-field dc-field--area">
        <span class="dc-label">Nave / Área</span>
        <v-autocomplete v-model="filtros.areas" :items="areas" item-text="nombre" item-value="idArea"
          placeholder="Toda la planta" multiple small-chips deletable-chips clearable :loading="loadingAreas"
          :disabled="loadingReporte" hide-details outlined dense class="dc-ac" />
      </div>

      <div class="dc-field dc-field--art">
        <span class="dc-label">Artículo (código)</span>
        <v-autocomplete v-model="filtros.idArticulo" :items="articulos" item-text="codigo" item-value="idArticulo"
          placeholder="Todos" clearable :loading="loadingArticulos" :disabled="loadingReporte"
          hide-details outlined dense class="dc-ac" />
      </div>

      <div class="dc-field dc-field--art">
        <span class="dc-label">Máquina</span>
        <v-autocomplete v-model="filtros.idMaquina" :items="maquinas" item-text="nombre" item-value="idMaquinaIns"
          placeholder="Todas" clearable :loading="loadingMaquinas" :disabled="loadingReporte"
          hide-details outlined dense class="dc-ac" />
      </div>

      <div class="dc-field">
        <span class="dc-label">Desde</span>
        <datePicker v-model="filtros.inicioDesde" format="YYYY-MM-DD" :disabled="loadingReporte" maxToday />
      </div>

      <div class="dc-field">
        <span class="dc-label">Hasta</span>
        <datePicker v-model="filtros.inicioHasta" format="YYYY-MM-DD" :disabled="loadingReporte" maxToday />
      </div>

      <div class="dc-field">
        <span class="dc-label">Período</span>
        <div class="dc-seg">
          <button type="button" class="dc-seg-btn" :class="{ 'is-on': periodoActivo === 'hoy' }"
            :disabled="loadingReporte" @click="setPeriodo('hoy')">Hoy</button>
          <button type="button" class="dc-seg-btn" :class="{ 'is-on': periodoActivo === 'semana' }"
            :disabled="loadingReporte" @click="setPeriodo('semana')">Semana</button>
          <button type="button" class="dc-seg-btn" :class="{ 'is-on': periodoActivo === 'mes' }"
            :disabled="loadingReporte" @click="setPeriodo('mes')">Mes</button>
        </div>
      </div>

      <div class="dc-actions">
        <v-progress-circular v-if="loadingReporte" indeterminate :size="20" :width="2" color="#2f6db0" />
        <button type="button" class="dc-btn dc-btn--excel" :disabled="!filtrosValidos || loadingReporte"
          @click="descargarExcel">
          <v-progress-circular v-if="descargando" indeterminate :size="15" :width="2" color="#0a7a4b" />
          <v-icon v-else small color="#0a7a4b">mdi-microsoft-excel</v-icon>
          Exportar
        </button>
      </div>
    </div>

    <LoaderData :loading="loadingReporte" :response="reporte.registros" :error="errorReporte">
      <template v-if="hayDatos">
        <!-- ===================== HERO ===================== -->
        <div class="def-hero">
          <div class="dh-kpis" style="display: flex; flex-wrap: nowrap; gap: 12px;">
            <div class="dh-kpi dh-kpi--brand" style="flex: 1 1 0; min-width: 0;">
              <div v-if="empresaDisplay" class="dh-brand">{{ empresaDisplay }}</div>
              <div class="dh-context">Incidencias · {{ ambitoTexto }}<template v-if="articuloTexto"> · {{ articuloTexto }}</template> · {{ periodoLabel }}</div>
            </div>
            <div class="dh-kpi" style="flex: 1 1 0; min-width: 0;">
              <div class="dh-kpi-k">Piezas de scrap registradas</div>
              <div class="dh-kpi-v dh-kpi-v--amber">{{ formatNumber(totalCantidadView) }}</div>
            </div>
          </div>
        </div>

        <!-- Foco activo (al hacer click en una barra): enfoca todas las gráficas a ese valor. -->
        <div v-if="foco" class="def-foco">
          <v-icon small color="#2f6db0">mdi-filter-variant</v-icon>
          <span class="def-foco-txt">Enfocado en {{ focoDimLabel }}: <b>{{ foco.valor }}</b></span>
          <button type="button" class="def-foco-x" @click="foco = null">✕ Quitar foco</button>
        </div>

        <!-- ===================== POR DÍA ===================== -->
        <section class="def-card">
          <header class="dcard-head">
            <div>
              <div class="dcard-kicker">Tendencia</div>
              <h3 class="dcard-title">Cantidad reportada por día</h3>
            </div>
          </header>
          <div class="dcard-body">
            <apexchart type="bar" height="320" :options="opcionesDia" :series="serieDia" />
          </div>
        </section>

        <!-- ===================== ARTÍCULO / INCIDENCIA ===================== -->
        <div class="def-grid2">
          <section class="def-card">
            <header class="dcard-head">
              <div>
                <div class="dcard-kicker">Artículo</div>
                <h3 class="dcard-title">Cantidad reportada por artículo</h3>
              </div>
            </header>
            <div class="dcard-body">
              <apexchart v-if="porArticulo.length" type="bar" height="360"
                :options="opcionesBar(porArticulo, '#e8833a', 'articulo')" :series="serieBar(porArticulo)" />
              <div v-else class="def-empty">Sin datos.</div>
            </div>
          </section>

          <section class="def-card">
            <header class="dcard-head">
              <div>
                <div class="dcard-kicker">Incidencia</div>
                <h3 class="dcard-title">Cantidad reportada por incidencias</h3>
              </div>
            </header>
            <div class="dcard-body">
              <apexchart v-if="porCausa.length" type="bar" height="360"
                :options="opcionesBar(porCausa, '#e5484d', 'incidencia')" :series="serieBar(porCausa)" />
              <div v-else class="def-empty">Sin datos.</div>
            </div>
          </section>
        </div>

        <!-- ===================== MÁQUINA / MOLDE ===================== -->
        <div class="def-grid2">
          <section class="def-card">
            <header class="dcard-head">
              <div>
                <div class="dcard-kicker">Máquina</div>
                <h3 class="dcard-title">Cantidad reportada por máquina</h3>
              </div>
            </header>
            <div class="dcard-body">
              <apexchart v-if="porMaquina.length" type="bar" height="360"
                :options="opcionesBar(porMaquina, '#8b5cf6', 'maquina')" :series="serieBar(porMaquina)" />
              <div v-else class="def-empty">Sin datos.</div>
            </div>
          </section>

          <section class="def-card">
            <header class="dcard-head">
              <div>
                <div class="dcard-kicker">Molde</div>
                <h3 class="dcard-title">Cantidad reportada por molde</h3>
              </div>
            </header>
            <div class="dcard-body">
              <apexchart v-if="porMolde.length" type="bar" height="360"
                :options="opcionesBar(porMolde, '#2f6db0', 'molde')" :series="serieBar(porMolde)" />
              <div v-else class="def-empty">Sin datos.</div>
            </div>
          </section>
        </div>
      </template>

      <div v-else class="def-empty def-empty--big">
        {{ consultado ? "Sin incidencias en el período seleccionado." : "Elige un período para ver las incidencias." }}
      </div>
    </LoaderData>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import LoaderData from "../../LoaderData.vue";

export default {
  components: {
    LoaderData,
  },
  props: {
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      areas: [],
      articulos: [],
      loadingAreas: false,
      loadingArticulos: false,
      loadingReporte: false,
      errorReporte: false,
      consultado: false,

      filtros: {
        areas: [],
        idArticulo: null,
        idMaquina: null,
        inicioDesde: null,
        inicioHasta: null,
      },

      maquinas: [],
      loadingMaquinas: false,

      reporte: {
        totalCantidad: 0,
        totalRegistros: 0,
        registros: [],
      },

      // Foco al hacer click en una barra: { dim, valor } enfoca TODAS las gráficas a ese valor.
      foco: null,

      periodoActivo: null,
      descargando: false,
      consultaTimer: null,
      fijandoPeriodo: false,

      // Hora de arranque del "día" de la empresa (jornada), de /Horarios/Reportes. Con esto los
      // botones Hoy/Semana/Mes calculan el rango de esa hora a esa hora (no de medianoche).
      // Default 06:00 (igual que el backend) hasta que cargue.
      horaJornada: { hh: 6, mm: 0 },
      jornadaTxt: "06:00",
    };
  },
  computed: {
    ...mapState(["nombreEmpresa"]),
    // La instancia "CAZEL-TLT" se muestra como "Tultitlán".
    empresaDisplay() {
      return this.nombreEmpresa === "CAZEL-TLT" ? "Tultitlán" : this.nombreEmpresa;
    },
    filtrosValidos() {
      if (!this.filtros.inicioDesde || !this.filtros.inicioHasta) return false;
      return this.filtros.inicioDesde <= this.filtros.inicioHasta;
    },
    hayDatos() {
      return this.reporte.registros.length > 0;
    },
    // Registros tras aplicar el foco (click en barra). Sin foco = todos.
    registrosFiltrados() {
      if (!this.foco) return this.reporte.registros;
      const campo = this.foco.dim;
      const valor = this.foco.valor;
      return this.reporte.registros.filter((r) => r[campo] === valor);
    },
    // Piezas de scrap del período (respeta el foco): suma de la cantidad reportada de las incidencias.
    totalCantidadView() {
      return this.registrosFiltrados.reduce((a, r) => a + this.toNumber(r.cantidad), 0);
    },
    focoDimLabel() {
      const map = { articulo: "artículo", maquina: "máquina", incidencia: "incidencia", molde: "molde" };
      return this.foco ? map[this.foco.dim] || this.foco.dim : "";
    },
    ambitoTexto() {
      if (this.filtros.areas.length) {
        const nombres = this.areas
          .filter((a) => this.filtros.areas.includes(a.idArea))
          .map((a) => a.nombre);
        return nombres.length ? nombres.join(", ") : "Naves seleccionadas";
      }
      return "Toda la planta";
    },
    articuloTexto() {
      if (!this.filtros.idArticulo) return "";
      const art = this.articulos.find((a) => a.idArticulo === this.filtros.idArticulo);
      return art ? art.codigo : "";
    },
    periodoLabel() {
      const d = this.filtros.inicioDesde;
      const h = this.filtros.inicioHasta;
      if (this.periodoActivo === "hoy") return `Hoy · ${this.fechaLarga(d)}`;
      if (this.periodoActivo === "semana") return `Semana · del ${this.fechaCorta(d)} al ${this.fechaCorta(h)}`;
      if (this.periodoActivo === "mes") return `Mes · ${this.nombreMes(d)}`;
      if (d && h) return `Del ${this.fechaCorta(d)} al ${this.fechaCorta(h)}`;
      return "Sin período";
    },
    // Agrupaciones calculadas desde los registros (respetando el foco), mayor impacto primero.
    porArticulo() {
      return this.agrupar("articulo");
    },
    porMaquina() {
      return this.agrupar("maquina");
    },
    porCausa() {
      return this.agrupar("incidencia");
    },
    porMolde() {
      return this.agrupar("molde");
    },
    // Por día de jornada (respeta el foco), en orden cronológico.
    porDiaAgrupado() {
      const map = new Map();
      this.registrosFiltrados.forEach((r) => {
        const key = r.diaJornada || "—";
        map.set(key, (map.get(key) || 0) + this.toNumber(r.cantidad));
      });
      return Array.from(map.entries())
        .map(([fecha, cantidad]) => ({ fecha, cantidad: Math.round(cantidad * 100) / 100 }))
        .sort((a, b) => (a.fecha < b.fecha ? -1 : 1));
    },
    serieDia() {
      return [{ name: "Cantidad reportada", data: this.porDiaAgrupado.map((d) => this.toNumber(d.cantidad)) }];
    },
    opcionesDia() {
      return {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.porDiaAgrupado.map((d) => this.fechaCorta(d.fecha)),
          title: { text: "Día" },
        },
        yaxis: { title: { text: "Cantidad reportada" }, labels: { formatter: (v) => this.formatNumber(v) } },
        colors: ["#2f6db0"],
        tooltip: { y: { formatter: (v) => this.formatNumber(v) } },
        grid: { borderColor: "#e4ebf5" },
      };
    },
  },
  watch: {
    active(newVal) {
      if (newVal && !this.consultado && this.filtrosValidos) this.consultar();
    },
    "filtros.inicioDesde"() {
      if (!this.fijandoPeriodo) this.periodoActivo = null;
      this.scheduleConsulta();
    },
    "filtros.inicioHasta"() {
      if (!this.fijandoPeriodo) this.periodoActivo = null;
      this.scheduleConsulta();
    },
    "filtros.areas": {
      handler() {
        this.scheduleConsulta();
      },
      deep: true,
    },
    "filtros.idArticulo"() {
      this.scheduleConsulta();
    },
    "filtros.idMaquina"() {
      this.scheduleConsulta();
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.getAreas();
      this.getArticulos();
      this.getMaquinas();
      // Carga la hora de jornada y, al terminar, fija el período por defecto (semana) ya con la
      // hora correcta. La consulta se dispara cuando la pestaña está activa.
      this.getHorarioJornada();
    },
    // Lee /Horarios/Reportes (endpoint sin permiso) y toma la hora de inicio del Horario con menor
    // día de la semana = arranque del "día" de la empresa. Mismo criterio que usa el backend.
    getHorarioJornada() {
      axios
        .get("/Horarios/Reportes")
        .then((response) => {
          const d = response.data || {};
          const eventos = Array.isArray(d.events) ? d.events : Array.isArray(d) ? d : [];
          if (eventos.length) {
            const menor = eventos.reduce((min, h) =>
              Number(h.diaSemana) < Number(min.diaSemana) ? h : min
            );
            const [hh, mm] = String(menor.inicio || "06:00").split(":").map((n) => parseInt(n, 10));
            if (Number.isFinite(hh)) {
              this.horaJornada = { hh, mm: Number.isFinite(mm) ? mm : 0 };
              const p2 = (n) => `${n}`.padStart(2, "0");
              this.jornadaTxt = `${p2(this.horaJornada.hh)}:${p2(this.horaJornada.mm)}`;
            }
          }
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.setPeriodo("semana");
        });
    },
    // Convierte una fecha "YYYY-MM-DD" al datetime de jornada "YYYY-MM-DDTHH:mm:00" (sumando
    // addDays días). Se usa para mandar al backend el rango con la hora de arranque de la empresa.
    toJornadaDT(dateStr, addDays) {
      if (!dateStr) return dateStr;
      const p = String(dateStr).split("-").map((n) => parseInt(n, 10));
      if (p.length < 3) return dateStr;
      const dt = new Date(p[0], p[1] - 1, p[2] + (addDays || 0), this.horaJornada.hh, this.horaJornada.mm, 0);
      const p2 = (n) => `${n}`.padStart(2, "0");
      return `${dt.getFullYear()}-${p2(dt.getMonth() + 1)}-${p2(dt.getDate())}T${p2(dt.getHours())}:${p2(dt.getMinutes())}:00`;
    },
    // Agrupa los registros (ya filtrados por el foco) sumando la cantidad por el campo dado,
    // ordenado de mayor a menor.
    agrupar(campo) {
      const map = new Map();
      this.registrosFiltrados.forEach((r) => {
        const key = r[campo] || "—";
        map.set(key, (map.get(key) || 0) + this.toNumber(r.cantidad));
      });
      return Array.from(map.entries())
        .map(([etiqueta, cantidad]) => ({ etiqueta, cantidad: Math.round(cantidad * 100) / 100 }))
        .sort((a, b) => b.cantidad - a.cantidad);
    },
    // Click en una barra: enfoca todas las gráficas a ese valor; volver a hacer click lo quita.
    setFoco(dim, valor) {
      if (this.foco && this.foco.dim === dim && this.foco.valor === valor) {
        this.foco = null;
      } else {
        this.foco = { dim, valor };
      }
    },
    toNumber(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    },
    formatNumber(value) {
      return this.toNumber(value).toLocaleString("en-US", { maximumFractionDigits: 2 });
    },
    serieBar(items) {
      return [{ name: "Cantidad reportada", data: items.map((i) => this.toNumber(i.cantidad)) }];
    },
    opcionesBar(items, color, dim) {
      return {
        chart: {
          type: "bar",
          toolbar: { show: false },
          events: {
            dataPointSelection: (event, chartContext, config) => {
              const it = items[config.dataPointIndex];
              if (it && dim) this.setFoco(dim, it.etiqueta);
            },
          },
        },
        plotOptions: { bar: { horizontal: false, borderRadius: 3, columnWidth: "60%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: items.map((i) => i.etiqueta),
          labels: {
            rotate: -45,
            rotateAlways: false,
            hideOverlappingLabels: true,
            trim: true,
            style: { fontSize: "11px" },
          },
        },
        yaxis: { labels: { formatter: (v) => this.formatNumber(v) } },
        colors: [color],
        tooltip: { y: { formatter: (v) => this.formatNumber(v) } },
        grid: { borderColor: "#eef2f8" },
      };
    },
    toDateInput(date) {
      const d = new Date(date);
      const month = `${d.getMonth() + 1}`.padStart(2, "0");
      const day = `${d.getDate()}`.padStart(2, "0");
      return `${d.getFullYear()}-${month}-${day}`;
    },
    fechaCorta(iso) {
      if (!iso) return "—";
      const p = String(iso).split("-");
      return p.length === 3 ? `${p[2]}/${p[1]}` : iso;
    },
    fechaLarga(iso) {
      if (!iso) return "—";
      const p = String(iso).split("-");
      return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
    },
    nombreMes(iso) {
      if (!iso) return "";
      const meses = [
        "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
      ];
      const p = String(iso).split("-");
      if (p.length < 2) return "";
      return `${meses[parseInt(p[1], 10) - 1] || ""} ${p[0]}`;
    },
    setPeriodo(tipo) {
      const hoy = new Date();
      let desde = new Date(hoy);
      if (tipo === "semana") {
        const dia = (hoy.getDay() + 6) % 7;
        desde = new Date(hoy);
        desde.setDate(hoy.getDate() - dia);
      } else if (tipo === "mes") {
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      }
      this.fijandoPeriodo = true;
      this.periodoActivo = tipo;
      this.filtros.inicioDesde = this.toDateInput(desde);
      this.filtros.inicioHasta = this.toDateInput(hoy);
      this.$nextTick(() => {
        this.fijandoPeriodo = false;
      });
      this.scheduleConsulta();
    },
    scheduleConsulta() {
      if (!this.active || !this.filtrosValidos) return;
      clearTimeout(this.consultaTimer);
      this.consultaTimer = setTimeout(() => this.consultar(), 300);
    },
    buildQuery() {
      const params = new URLSearchParams();
      this.filtros.areas.forEach((id) => params.append("idArea", id));
      // Rango con la hora de jornada: desde = día@hora, hasta = (últimoDía+1)@hora (exclusivo).
      params.append("inicioDesde", this.toJornadaDT(this.filtros.inicioDesde, 0));
      params.append("inicioHasta", this.toJornadaDT(this.filtros.inicioHasta, 1));
      if (this.filtros.idArticulo) params.append("idArticulo", this.filtros.idArticulo);
      if (this.filtros.idMaquina) params.append("idMaquina", this.filtros.idMaquina);
      return params.toString();
    },
    consultar() {
      if (!this.filtrosValidos) return;
      this.loadingReporte = true;
      this.errorReporte = false;
      this.foco = null; // nuevo dataset: se limpia el foco anterior
      axios
        .get(`/AnalisisDefectos?${this.buildQuery()}`)
        .then((response) => {
          this.consultado = true;
          const d = response.data || {};
          this.reporte = {
            totalCantidad: this.toNumber(d.totalCantidad),
            totalRegistros: this.toNumber(d.totalRegistros),
            registros: Array.isArray(d.registros) ? d.registros : [],
          };
        })
        .catch((error) => {
          console.log(error);
          this.errorReporte = true;
        })
        .finally(() => {
          this.loadingReporte = false;
        });
    },
    descargarExcel() {
      if (!this.filtrosValidos || this.descargando) return;
      this.descargando = true;
      const params = new URLSearchParams();
      this.filtros.areas.forEach((id) => params.append("idArea", id));
      params.append("inicioDesde", this.toJornadaDT(this.filtros.inicioDesde, 0));
      params.append("inicioHasta", this.toJornadaDT(this.filtros.inicioHasta, 1));
      if (this.filtros.idArticulo) params.append("idArticulo", this.filtros.idArticulo);
      if (this.filtros.idMaquina) params.append("idMaquina", this.filtros.idMaquina);
      if (this.empresaDisplay) params.append("empresa", this.empresaDisplay);
      axios
        .get(`/AnalisisDefectos/Excel?${params.toString()}`, { responseType: "blob" })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = URL.createObjectURL(blob);
          const enlace = document.createElement("a");
          enlace.href = url;
          enlace.download = "AnalisisIncidencias.xlsx";
          document.body.appendChild(enlace);
          enlace.click();
          document.body.removeChild(enlace);
          URL.revokeObjectURL(url);
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.descargando = false;
        });
    },
    getAreas() {
      this.loadingAreas = true;
      axios
        .get("/Areas")
        .then((response) => {
          const data = Array.isArray(response.data) ? response.data : [];
          this.areas = data.filter((a) => a.idArea > 1);
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.loadingAreas = false;
        });
    },
    getArticulos() {
      this.loadingArticulos = true;
      axios
        .get("/Articulos/ArticulosNombres?componentes=true")
        .then((response) => {
          this.articulos = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.loadingArticulos = false;
        });
    },
    getMaquinas() {
      this.loadingMaquinas = true;
      axios
        .get("/MaquinaInstancias")
        .then((response) => {
          this.maquinas = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.loadingMaquinas = false;
        });
    },
  },
};
</script>

<style scoped>
.def {
  --line: #e4eaf3;
  --muted: #66768d;
  --text: #16202f;
  --amber-2: #e8833a;
  --red: #e5484d;
  --steel: #2f6db0;
  color: var(--text);
  font-family: 'Space Grotesk', 'Roboto', 'Segoe UI', sans-serif;
}

/* ---------- Barra de control ---------- */
.def-control {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 12px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 6px 22px rgba(20, 40, 80, 0.05);
}

.dc-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 132px;
  min-width: 120px;
}

.dc-field--area {
  flex: 1 1 160px;
  min-width: 150px;
  max-width: 240px;
}

.dc-field--art {
  flex: 1 1 160px;
  min-width: 150px;
  max-width: 240px;
}

.dc-label {
  color: var(--muted);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.dc-field ::v-deep .field-input,
.dc-field ::v-deep .v-input__slot {
  background: #f6f8fc !important;
  border-radius: 12px !important;
  min-height: 44px !important;
}

.dc-field ::v-deep fieldset {
  border-color: var(--line) !important;
  border-radius: 12px !important;
}

.dc-field ::v-deep input,
.dc-field ::v-deep .v-select__selection {
  color: var(--text) !important;
}

.dc-field ::v-deep .v-chip {
  background: #e5edf8 !important;
  color: var(--text) !important;
  font-weight: 600;
}

/* ---------- Segmented ---------- */
.dc-seg {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 2px;
  background: #eef2f8;
  border: 1px solid #e0e7f1;
  border-radius: 13px;
  padding: 3px;
}

.dc-seg-btn {
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #55647b;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  padding: 9px 12px;
  border-radius: 10px;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.dc-seg-btn.is-on {
  background: #0c1220;
  color: #fff;
  box-shadow: 0 3px 8px rgba(12, 18, 32, 0.28);
}

.dc-seg-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.dc-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  margin-left: auto;
}

.dc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 44px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  border: 1px solid transparent;
  border-radius: 13px;
  padding: 0 18px;
  transition: transform 0.12s ease, background 0.15s ease;
}

.dc-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.dc-btn--excel {
  background: #eafaf1;
  border-color: #b8e6cf;
  color: #0a7a4b;
}

.dc-btn--excel:hover:not(:disabled) {
  background: #d9f5e6;
}

/* ---------- Hero ---------- */
.def-hero {
  margin-top: 16px;
  padding: 20px 22px;
  border: 1px solid var(--line);
  border-top: 3px solid var(--amber-2);
  border-radius: 20px;
  background: linear-gradient(180deg, #fbfcfe, #ffffff);
  box-shadow: 0 6px 22px rgba(20, 40, 80, 0.06);
}

.dh-kpis {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
}

.dh-kpi {
  flex: 1 1 0;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: #f6f8fc;
}

.dh-kpi--brand {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.dh-kpi-k {
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.dh-kpi-v {
  font-family: 'JetBrains Mono', 'SFMono-Regular', 'Consolas', monospace;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.1;
  margin-top: 5px;
}

.dh-kpi-v--amber {
  color: var(--amber-2);
}

.dh-brand {
  align-self: flex-start;
  color: var(--text);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 2px solid var(--amber-2);
}

.dh-context {
  color: var(--muted);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.45;
}

/* ---------- Foco (cross-filter al hacer click en una barra) ---------- */
.def-foco {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  padding: 10px 14px;
  background: #eef3fb;
  border: 1px solid #d6e2f2;
  border-radius: 12px;
  font-size: 13px;
}

.def-foco-txt {
  color: var(--text);
  font-weight: 600;
}

.def-foco-x {
  margin-left: auto;
  border: 1px solid #cdd9ea;
  background: #fff;
  color: var(--steel);
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
}

.def-foco-x:hover {
  background: #e2ecf8;
}

/* ---------- Cards ---------- */
.def-card {
  margin-top: 16px;
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 18px;
  box-shadow: 0 6px 22px rgba(20, 40, 80, 0.05);
}

.def-grid2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 16px;
}

@media (max-width: 720px) {
  .def-grid2 {
    grid-template-columns: 1fr;
  }
}

.def-grid2 .def-card {
  margin-top: 0;
}

.dcard-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #eef2f8;
}

.dcard-kicker {
  color: var(--amber-2);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.dcard-title {
  color: var(--text);
  font-size: 1.12rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 3px 0 0;
}

.dcard-body {
  padding: 12px 14px;
}

.def-empty {
  color: var(--muted);
  font-size: 0.95rem;
  padding: 24px 14px;
  text-align: center;
}

.def-empty--big {
  margin-top: 16px;
  padding: 52px 16px;
  font-size: 1.05rem;
  background: #fff;
  border: 1px dashed var(--line);
  border-radius: 18px;
}
</style>
